import { useState, useRef, useEffect } from 'react';
import { Box, TextField, IconButton, Typography } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import { ExecutionTimeline } from './ExecutionTimeline';
import { useOrchestratorStream } from './useOrchestratorStream';
import { sessionService } from '../../services/sessionService';
import { useNotificationStore } from '../../store/notificationStore';

interface OrchestratorChatProps {
  agentId: string;
  agentName: string;
}

export const OrchestratorChat = ({ agentId, agentName }: OrchestratorChatProps) => {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const { events, isRunning, streamingText, reset } = useOrchestratorStream(sessionId);
  const { showNotification } = useNotificationStore();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [events.length, streamingText]);

  const handleSend = async () => {
    if (!input.trim() || sending || isRunning) return;

    try {
      setSending(true);
      let sid = sessionId;

      if (!sid) {
        const session = await sessionService.createSession({
          agentDefinitionId: agentId,
          sessionName: `Orchestrator: ${agentName}`,
        });
        sid = session.id;
        setSessionId(sid);
      }

      reset();
      await sessionService.sendMessage(sid, { content: input });
      setInput('');
    } catch (err: any) {
      showNotification(err.message || 'Failed to send message', 'error');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Timeline area */}
      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          overflow: 'auto',
          bgcolor: 'background.default',
        }}
      >
        {events.length === 0 && !isRunning && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <Typography variant="body2" color="text.secondary">
              Send a task to the orchestrator. It will delegate work to attached sub-agents.
            </Typography>
          </Box>
        )}
        <ExecutionTimeline events={events} streamingText={streamingText} isRunning={isRunning} />
      </Box>

      {/* Input */}
      <Box sx={{ borderTop: 1, borderColor: 'divider', p: 1.5, display: 'flex', gap: 1 }}>
        <TextField
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Describe your task..."
          fullWidth
          size="small"
          multiline
          maxRows={4}
          disabled={sending || isRunning}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!input.trim() || sending || isRunning}
        >
          <SendIcon />
        </IconButton>
      </Box>
    </Box>
  );
};
